import { useState, useEffect, useCallback } from 'react';
import sectionCommentService from '../services/sectionCommentService';

/**
 * Hook for managing inline comments on sections within a conversation.
 *
 * Returns:
 *   commentCounts   — { sectionId: { open, resolved, total } }
 *   sectionComments — comments array for the currently open section
 *   openSectionId   — which section's comment panel is open
 *   openCommentPanel(sectionId) — open the panel for a section
 *   closeCommentPanel() — close the panel
 *   addComment(data)   — create a comment
 *   replyToComment(parentId, content) — reply
 *   resolveComment(id) — mark as resolved
 *   reopenComment(id)  — reopen
 *   deleteComment(id)  — delete
 *   refreshCounts()    — refresh badge counts
 *   isLoading          — loading state
 */
const useInlineComments = ({ projectId, conversationId, taskId }) => {
  const [commentCounts, setCommentCounts] = useState({});
  const [sectionComments, setSectionComments] = useState([]);
  const [openSectionId, setOpenSectionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch comment counts for all sections (badges)
  const refreshCounts = useCallback(async () => {
    if (!conversationId || !taskId) return;
    try {
      const counts = await sectionCommentService.getCommentCounts(conversationId, taskId);
      setCommentCounts(counts || {});
    } catch {
      // Silent — badge counts are supplementary
    }
  }, [conversationId, taskId]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  // Fetch comments for a specific section
  const fetchSectionComments = useCallback(async (sectionId) => {
    if (!conversationId || !sectionId) return;
    setIsLoading(true);
    try {
      const comments = await sectionCommentService.getSectionComments(
        conversationId, sectionId, true
      );
      setSectionComments(comments || []);
    } catch {
      setSectionComments([]);
    }
    setIsLoading(false);
  }, [conversationId]);

  // Open comment panel for a section
  const openCommentPanel = useCallback((sectionId) => {
    setOpenSectionId(sectionId);
    fetchSectionComments(sectionId);
  }, [fetchSectionComments]);

  // Close panel
  const closeCommentPanel = useCallback(() => {
    setOpenSectionId(null);
    setSectionComments([]);
  }, []);

  // Add a new comment
  const addComment = useCallback(async ({
    sectionId, content, fieldPath, selectedText, textAnchor
  }) => {
    if (!projectId || !conversationId) return null;
    try {
      const comment = await sectionCommentService.createComment(projectId, {
        conversation_id: conversationId,
        task_id: taskId,
        section_id: sectionId,
        content,
        field_path: fieldPath || null,
        selected_text: selectedText || null,
        text_anchor: textAnchor || null,
      });
      // Refresh
      if (openSectionId === sectionId) {
        fetchSectionComments(sectionId);
      }
      refreshCounts();
      return comment;
    } catch (err) {
      console.error('Failed to add comment:', err);
      return null;
    }
  }, [projectId, conversationId, taskId, openSectionId, fetchSectionComments, refreshCounts]);

  // Reply to a comment
  const replyToComment = useCallback(async (parentId, content) => {
    if (!projectId) return null;
    try {
      const comment = await sectionCommentService.createComment(projectId, {
        conversation_id: conversationId,
        task_id: taskId,
        section_id: openSectionId || '',
        content,
        parent_id: parentId,
      });
      if (openSectionId) {
        fetchSectionComments(openSectionId);
      }
      refreshCounts();
      return comment;
    } catch (err) {
      console.error('Failed to reply:', err);
      return null;
    }
  }, [projectId, conversationId, taskId, openSectionId, fetchSectionComments, refreshCounts]);

  // Resolve
  const resolveComment = useCallback(async (commentId) => {
    try {
      await sectionCommentService.resolveComment(commentId);
      if (openSectionId) fetchSectionComments(openSectionId);
      refreshCounts();
    } catch (err) {
      console.error('Failed to resolve:', err);
    }
  }, [openSectionId, fetchSectionComments, refreshCounts]);

  // Reopen
  const reopenComment = useCallback(async (commentId) => {
    try {
      await sectionCommentService.reopenComment(commentId);
      if (openSectionId) fetchSectionComments(openSectionId);
      refreshCounts();
    } catch (err) {
      console.error('Failed to reopen:', err);
    }
  }, [openSectionId, fetchSectionComments, refreshCounts]);

  // Delete
  const deleteComment = useCallback(async (commentId) => {
    try {
      await sectionCommentService.deleteComment(commentId);
      if (openSectionId) fetchSectionComments(openSectionId);
      refreshCounts();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  }, [openSectionId, fetchSectionComments, refreshCounts]);

  return {
    commentCounts,
    sectionComments,
    openSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment,
    reopenComment,
    deleteComment,
    refreshCounts,
    isLoading,
  };
};

export default useInlineComments;
